import type { AxiosInstance } from 'axios';
import { action, makeAutoObservable, runInAction } from 'mobx';
import { NotificationsStore } from './NotificationsStore';
import IAnalyticsStore from 'stores/AnalyticsStore';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';

// types
import { IResult } from 'services/Result';
import { SingleUserType } from 'types/GlobalTypes';
import { UserData } from 'types/models/UserData';

interface IUsersStore {
  users: SingleUserType[];
  selectedUser: SingleUserType | null;
  isUsersDialogOpened: boolean;

  isLoadingUsers: boolean;
  isRevokingAccess: boolean;
  isGrantingAccess: boolean;
  isSearchingForNewUser: boolean;

  setSelectedUser(user: SingleUserType | null): void;
  getUsers(apiInstance: AxiosInstance, orgid: string): void;
  displayUsersDialog(isOpen: boolean): void;
  findNewUserByEmail(apiInstance: AxiosInstance, email: string): Promise<UserData[] | null>;
  ReassignTrackmanUser(apiInstance: AxiosInstance, content: IReassignRequest): Promise<boolean>;
  RevokeUser(apiInstance: AxiosInstance, content: IRevokeAccess): Promise<boolean>;
}

export interface IReassignRequest {
  userId: string;
  organizationId: string;
  roleName: string;
}

export interface IRevokeAccess {
  userId: string;
  organizationId: string;
}

export class UsersStore implements IUsersStore {
  users: SingleUserType[] = [];
  selectedUser: SingleUserType | null = null;

  isUsersDialogOpened: boolean = false;

  isLoadingUsers: boolean = false;
  isRevokingAccess: boolean = false;
  isGrantingAccess: boolean = false;
  isSearchingForNewUser: boolean = false;

  _notifications_store: NotificationsStore;
  _analytics_store: IAnalyticsStore;

  constructor(notifications_store: NotificationsStore, analytics_store: IAnalyticsStore) {
    this._notifications_store = notifications_store;
    this._analytics_store = analytics_store;
    makeAutoObservable(this);
  }

  @action
  public setSelectedUser = (user: SingleUserType | null) => {
    this.selectedUser = user;
  };

  @action
  public displayUsersDialog = (isOpen: boolean) => {
    this.isUsersDialogOpened = isOpen;
  };

  @action
  public async findNewUserByEmail(apiInstance: AxiosInstance, email: string) {
    this.isSearchingForNewUser = true;
    const response = await apiInstance.post<IResult<UserData[]>>(`api/identity/find`, { emails: [email] });
    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
      this.isSearchingForNewUser = false;
      return response.data.data;
    } else {
      let errorMsg = `Cannot find user with email ${email}.`;
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data);
      this._notifications_store.addToast(
        'Error',
        errorMsg,
        'error'
      );
    }
    this.isSearchingForNewUser = false;
    return null;
  }

  @action
  public async getUsers(apiInstance: AxiosInstance, orgId: string) {
    this.isLoadingUsers = true;
    const response = await apiInstance.get<IResult<{ id: string; users: SingleUserType[] }>>(`organization/${orgId}/users`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
      this.users = response.data.data?.users;
    } else {
      let errorMsg = 'Cannot retrieve users list.'
      AxiosResponseHelper.ReportErrors(this._analytics_store, `Failed to retrieve users list.`, response.data);
      this._notifications_store.addToast(
        'Error',
        errorMsg,
        'error'
      );
    }
    this.isLoadingUsers = false;
  }

  @action
  public async ReassignTrackmanUser(apiInstance: AxiosInstance, content: IReassignRequest): Promise<boolean> {
    runInAction(() => {
      this.isGrantingAccess = true;
    });

    const response = await apiInstance.post<IResult<any>>('api/access/reassignTrackmanUser', content);

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      this._analytics_store.sendGrantUserEvent(`Grant user: ${content.userId} permissions`)
      this._notifications_store.addToast(
        'Success',
        'User has been granted access.',
        'success'
      );

      runInAction(() => {
        this.isGrantingAccess = false;
      });

      return true;
    }

    AxiosResponseHelper.ReportErrors(this._analytics_store, `Failed to grant user: ${content.userId} permissions`, response.data);
    this._notifications_store.addToast(
      'Error',
      'Failed to grant permissions.',
      'error'
    );

    runInAction(() => {
      this.isGrantingAccess = false;
    });

    return false;
  }

  @action
  public async RevokeUser(apiInstance: AxiosInstance, content: IRevokeAccess): Promise<boolean> {
    runInAction(() => {
      this.isRevokingAccess = true;
    });

    const response = await apiInstance.post<IResult<any>>('api/access/revoke', content);

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      this._analytics_store.sendRevokeUserEvent(`Revoke user: ${content.userId} permissions`)
      this._notifications_store.addToast(
        'Success',
        'User has been revoked access.',
        'success'
      );

      runInAction(() => {
        this.isRevokingAccess = false;
      });
      return true;
    }

    AxiosResponseHelper.ReportErrors(this._analytics_store, `Failed to revoke user: ${content.userId} permissions`, response.data);
    this._notifications_store.addToast(
      'Error',
      'Failed to revoke permissions.',
      'error'
    );

    runInAction(() => {
      this.isRevokingAccess = false;
    });

    return false;
  }
}
